import React from 'react';
import { MonthYear } from '../../Calendar';
import {
    AuditLogEvent,
    CalendarEventHelper,
    VisitEvent,
    eventTypeColorMap,
    getColorForAuditLogType,
} from '../useCalendarEvents';
import { getIntl } from '../../../../utils/intl';
import { EventType } from '../types';
import Stack, { contentStackSpacing } from '../../Stack';
import Text from '../../Text';
import PressableDynamicOpacity from '../../PressableDynamicOpacity';
import { useProfilesTabNavigation } from '../../../../utils/appNavigationNames';
import { useResourceTitle } from '../../../../utils/resourceUtils/getResourceTitle';
import getDateString from '../../../../utils/getDateString';
import { PRScrollView } from '../../../shared/Scroll';
import ActivityListDateDisplay from './ActivityListDateDisplay';

const intl = getIntl('activityListContent', {
    noActivity: 'No activity for this month',
});

export type ActivityListContentOwnProps = {
    monthYear: MonthYear;
    calendarEventHelper: CalendarEventHelper;
    eventType: EventType;
    profileId: string;
    placeholderHeight: number;
};

export type ActivityListContentProps = ActivityListContentOwnProps;

const ListItemContainer: React.FC<{
    onPress: () => void;
    leftContent: React.ReactNode;
    title: string;
    subTitle: string;
}> = (props) => {
    return (
        <PressableDynamicOpacity
            onPress={props.onPress}
            style={{ alignSelf: 'stretch' }}
            testID="calendar-list-activity-item"
        >
            <Stack horizontal align="flex-start" space={4}>
                {props.leftContent}
                <Stack vertical style={{ alignSelf: 'stretch' }} justify="center" space={2} flex={1}>
                    <Text variant="bold-body-1" wrapText>
                        {props.title}
                    </Text>
                    <Text variant="caption-1">{props.subTitle}</Text>
                </Stack>
            </Stack>
        </PressableDynamicOpacity>
    );
};

const VisitItem: React.FC<{
    event: VisitEvent;
    profileId: string;
}> = ({ event, profileId }) => {
    const { navigate } = useProfilesTabNavigation();
    const visitTitle = useResourceTitle(event.visit, profileId);
    return (
        <ListItemContainer
            onPress={() =>
                navigate('data-details', {
                    resourceId: event.visit.id,
                    profileId,
                    category: 'visit',
                })
            }
            leftContent={
                <ActivityListDateDisplay dateISO={event.visit.startDateTime!} color={eventTypeColorMap['visits']} />
            }
            title={visitTitle}
            subTitle={getDateString(event.visit.startDateTime, 'time')}
        />
    );
};

const AuditLogEventItem: React.FC<{
    event: AuditLogEvent;
    profileId: string;
}> = ({ event, profileId }) => {
    const { navigate } = useProfilesTabNavigation();
    const dateISO = new Date(event.auditLog.timestamp).toISOString();
    return (
        <ListItemContainer
            onPress={() =>
                navigate('profile-activity-details', {
                    auditLogId: event.auditLog.auditLogId,
                    profileId,
                })
            }
            leftContent={
                <ActivityListDateDisplay dateISO={dateISO} color={getColorForAuditLogType(event.auditLog.type)} />
            }
            title={event.auditLog.auditLogTitle}
            subTitle={getDateString(dateISO, 'time')}
        />
    );
};

const ActivityListContent: React.FC<ActivityListContentProps> = (props) => {
    const events = props.calendarEventHelper.getMonthYearEvents(props.monthYear, props.eventType);

    if (!events.length) {
        return (
            <Stack
                vertical
                style={{ height: props.placeholderHeight }}
                fullWidth
                align="center"
                justify="center"
                padding={4}
                testID="no-activity"
            >
                <Text variant="bold-h5" color="Gray-Text" alignText="center">
                    {intl('noActivity')}
                </Text>
            </Stack>
        );
    } else {
        return (
            <PRScrollView
                contentContainerStyle={{ flexGrow: 1, padding: 8 }}
                style={{
                    alignSelf: 'stretch',
                }}
            >
                <Stack space={contentStackSpacing} vertical fullWidth>
                    {events.map((event) => {
                        if (event.type === 'visit') {
                            return <VisitItem key={event.visit.id} event={event} profileId={props.profileId} />;
                        } else {
                            return (
                                <AuditLogEventItem
                                    key={event.auditLog.auditLogId}
                                    event={event}
                                    profileId={props.profileId}
                                />
                            );
                        }
                    })}
                </Stack>
            </PRScrollView>
        );
    }
};

export default React.memo(ActivityListContent);
